import {
  PNPG_CONDITIONS,
  PNPG_RULES,
  PNPG_RULE_STATUS,
  PNPG_SOURCE,
  PNPG_ZONE_TYPES,
} from "../data/pnpgProtectionRules.js";

const EARTH_RADIUS_M = 6371008.8;
const EPSILON = 1e-10;

const STATUS_WEIGHT = Object.freeze({
  [PNPG_RULE_STATUS.NOT_APPLICABLE]: 0,
  [PNPG_RULE_STATUS.ALLOWED]: 1,
  [PNPG_RULE_STATUS.NEEDS_CONFIRMATION]: 2,
  [PNPG_RULE_STATUS.AUTHORIZATION_REQUIRED]: 3,
  [PNPG_RULE_STATUS.PROHIBITED]: 4,
});

export function normalizeZoneType(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  const aliases = {
    total: PNPG_ZONE_TYPES.TOTAL,
    protecao_total: PNPG_ZONE_TYPES.TOTAL,
    partial_i: PNPG_ZONE_TYPES.PARTIAL_I,
    parcial_i: PNPG_ZONE_TYPES.PARTIAL_I,
    protecao_parcial_tipo_i: PNPG_ZONE_TYPES.PARTIAL_I,
    partial_ii: PNPG_ZONE_TYPES.PARTIAL_II,
    parcial_ii: PNPG_ZONE_TYPES.PARTIAL_II,
    protecao_parcial_tipo_ii: PNPG_ZONE_TYPES.PARTIAL_II,
    complementary_i: PNPG_ZONE_TYPES.COMPLEMENTARY_I,
    complementar_i: PNPG_ZONE_TYPES.COMPLEMENTARY_I,
    protecao_complementar_tipo_i: PNPG_ZONE_TYPES.COMPLEMENTARY_I,
    complementary_ii: PNPG_ZONE_TYPES.COMPLEMENTARY_II,
    complementar_ii: PNPG_ZONE_TYPES.COMPLEMENTARY_II,
    protecao_complementar_tipo_ii: PNPG_ZONE_TYPES.COMPLEMENTARY_II,
    unprotected: PNPG_ZONE_TYPES.UNPROTECTED,
    sem_regime: PNPG_ZONE_TYPES.UNPROTECTED,
  };
  return aliases[normalized] || null;
}

function resolveThresholdRule(rule, participantCount) {
  if (!rule || rule.kind !== "participant_threshold") return rule;
  const count = Number(participantCount);
  if (!Number.isFinite(count) || count < 1) {
    return {
      status: PNPG_RULE_STATUS.NEEDS_CONFIRMATION,
      conditions: rule.whenTrue?.conditions || [],
      note: "É necessário indicar o número de participantes.",
    };
  }
  const matches = rule.operator === ">" && count > rule.participants;
  return matches ? rule.whenTrue : rule.whenFalse;
}

export function evaluatePnpgRule({
  zoneType,
  activity,
  participantCount = null,
  organized = false,
} = {}) {
  const zone = normalizeZoneType(zoneType);
  if (!zone || zone === PNPG_ZONE_TYPES.UNPROTECTED) {
    return {
      status: PNPG_RULE_STATUS.NEEDS_CONFIRMATION,
      zoneType: zone,
      activity,
      conditions: [PNPG_CONDITIONS.CHECK_TEMPORARY_RESTRICTIONS],
      reason: zone
        ? "Zona sem regime específico na planta; mantêm-se aplicáveis as regras gerais do POPNPG."
        : "Tipo de zona desconhecido.",
      source: PNPG_SOURCE,
    };
  }

  const rawRule = PNPG_RULES?.[activity]?.[zone];
  if (!rawRule) {
    return {
      status: PNPG_RULE_STATUS.NEEDS_CONFIRMATION,
      zoneType: zone,
      activity,
      conditions: [PNPG_CONDITIONS.CHECK_TEMPORARY_RESTRICTIONS],
      reason: "A atividade não está coberta pela tabela resumida.",
      source: PNPG_SOURCE,
    };
  }

  let resolved = resolveThresholdRule(rawRule, participantCount);
  const count = Number(participantCount);
  const generalGroupAuthorization =
    Number.isFinite(count) && count > 15 && resolved.status === PNPG_RULE_STATUS.ALLOWED;
  if (generalGroupAuthorization || (organized && resolved.status === PNPG_RULE_STATUS.ALLOWED)) {
    resolved = {
      ...resolved,
      status: PNPG_RULE_STATUS.AUTHORIZATION_REQUIRED,
      note: generalGroupAuthorization
        ? "O artigo 8.º sujeita a autorização atividades turísticas com mais de 15 participantes."
        : "Eventos organizados podem estar sujeitos a autorização nos termos do artigo 8.º e da Carta de Desporto de Natureza.",
    };
  }

  return {
    ...resolved,
    zoneType: zone,
    activity,
    participantCount: Number.isFinite(count) ? count : null,
    conditions: [...new Set(resolved.conditions || [])],
    source: PNPG_SOURCE,
  };
}

export function combineZoneAssessments(assessments = []) {
  const valid = assessments.filter(Boolean);
  if (!valid.length) return null;
  const worst = valid.reduce((selected, current) =>
    (STATUS_WEIGHT[current.status] || 0) > (STATUS_WEIGHT[selected.status] || 0)
      ? current
      : selected
  );
  return {
    status: worst.status,
    zones: valid,
    conditions: [...new Set(valid.flatMap((item) => item.conditions || []))],
    requiresAuthorization: valid.some(
      (item) => item.status === PNPG_RULE_STATUS.AUTHORIZATION_REQUIRED
    ),
    prohibited: valid.some((item) => item.status === PNPG_RULE_STATUS.PROHIBITED),
    source: PNPG_SOURCE,
  };
}

function normalizeCoordinate(point) {
  if (Array.isArray(point) && point.length >= 2) {
    const lon = Number(point[0]);
    const lat = Number(point[1]);
    return Number.isFinite(lat) && Number.isFinite(lon) ? [lon, lat] : null;
  }
  const lat = Number(point?.lat ?? point?.latitude);
  const lon = Number(point?.lon ?? point?.lng ?? point?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lon) ? [lon, lat] : null;
}

function pointOnSegment(point, a, b) {
  const lengthSq = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
  if (lengthSq <= EPSILON) {
    return Math.hypot(point[0] - a[0], point[1] - a[1]) <= EPSILON;
  }
  const cross =
    (point[1] - a[1]) * (b[0] - a[0]) - (point[0] - a[0]) * (b[1] - a[1]);
  if (Math.abs(cross) > EPSILON) return false;
  const dot =
    (point[0] - a[0]) * (b[0] - a[0]) + (point[1] - a[1]) * (b[1] - a[1]);
  if (dot < -EPSILON) return false;
  return dot <= lengthSq + EPSILON;
}

function pointInRing(point, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j];
    const b = ring[i];
    if (pointOnSegment(point, a, b)) return true;
    const crosses =
      (a[1] > point[1]) !== (b[1] > point[1]) &&
      point[0] <
        ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0];
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointInPolygon(point, polygon) {
  if (!polygon?.[0] || !pointInRing(point, polygon[0])) return false;
  return !polygon.slice(1).some((hole) => pointInRing(point, hole));
}

function polygonList(geometry) {
  if (geometry?.type === "Polygon") return [geometry.coordinates];
  if (geometry?.type === "MultiPolygon") return geometry.coordinates;
  return [];
}

const geometryBoundsCache = new WeakMap();

function geometryBounds(feature) {
  if (Array.isArray(feature?.bbox) && feature.bbox.length >= 4) {
    const bbox = feature.bbox.slice(0, 4).map(Number);
    if (bbox.every(Number.isFinite)) return bbox;
  }
  if (feature && geometryBoundsCache.has(feature)) return geometryBoundsCache.get(feature);
  const bbox = [Infinity, Infinity, -Infinity, -Infinity];
  for (const polygon of polygonList(feature?.geometry)) {
    for (const ring of polygon) {
      for (const point of ring) {
        const lon = Number(point?.[0]);
        const lat = Number(point?.[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        bbox[0] = Math.min(bbox[0], lon);
        bbox[1] = Math.min(bbox[1], lat);
        bbox[2] = Math.max(bbox[2], lon);
        bbox[3] = Math.max(bbox[3], lat);
      }
    }
  }
  const result = bbox.every(Number.isFinite) ? bbox : null;
  if (feature && result) geometryBoundsCache.set(feature, result);
  return result;
}

function routeBounds(points, paddingMeters = 0) {
  const bbox = [Infinity, Infinity, -Infinity, -Infinity];
  points.forEach(([lon, lat]) => {
    bbox[0] = Math.min(bbox[0], lon);
    bbox[1] = Math.min(bbox[1], lat);
    bbox[2] = Math.max(bbox[2], lon);
    bbox[3] = Math.max(bbox[3], lat);
  });
  const centerLat = (bbox[1] + bbox[3]) / 2;
  const latPad = Math.max(0, Number(paddingMeters) || 0) / 110540;
  const lonPad =
    Math.max(0, Number(paddingMeters) || 0) /
    Math.max(20000, 111320 * Math.cos((centerLat * Math.PI) / 180));
  return [bbox[0] - lonPad, bbox[1] - latPad, bbox[2] + lonPad, bbox[3] + latPad];
}

function boundsIntersect(a, b) {
  return !!a && !!b && a[2] >= b[0] && a[0] <= b[2] && a[3] >= b[1] && a[1] <= b[3];
}

function pointInGeometry(point, geometry) {
  return polygonList(geometry).some((polygon) => pointInPolygon(point, polygon));
}

function segmentIntersectionT(a, b, c, d) {
  const r = [b[0] - a[0], b[1] - a[1]];
  const s = [d[0] - c[0], d[1] - c[1]];
  const cross = (u, v) => u[0] * v[1] - u[1] * v[0];
  const denominator = cross(r, s);
  const ca = [c[0] - a[0], c[1] - a[1]];
  if (Math.abs(denominator) < EPSILON) return [];
  const t = cross(ca, s) / denominator;
  const u = cross(ca, r) / denominator;
  return t >= -EPSILON && t <= 1 + EPSILON && u >= -EPSILON && u <= 1 + EPSILON
    ? [Math.max(0, Math.min(1, t))]
    : [];
}

function boundaryIntersections(a, b, geometry) {
  const values = [];
  for (const polygon of polygonList(geometry)) {
    for (const ring of polygon) {
      for (let index = 1; index < ring.length; index += 1) {
        values.push(...segmentIntersectionT(a, b, ring[index - 1], ring[index]));
      }
      if (ring.length > 2) {
        values.push(...segmentIntersectionT(a, b, ring[ring.length - 1], ring[0]));
      }
    }
  }
  return [...new Set(values.map((value) => value.toFixed(10)))].map(Number);
}

export function haversineMeters(aValue, bValue) {
  const a = normalizeCoordinate(aValue);
  const b = normalizeCoordinate(bValue);
  if (!a || !b) return 0;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function interpolate(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function projectMeters(point, referenceLatitude) {
  const latitudeRadians = (referenceLatitude * Math.PI) / 180;
  return [
    point[0] * 111320 * Math.cos(latitudeRadians),
    point[1] * 110540,
  ];
}

function pointToSegmentDistance(point, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= EPSILON) return Math.hypot(point[0] - a[0], point[1] - a[1]);
  const t = Math.max(
    0,
    Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / lengthSquared)
  );
  return Math.hypot(point[0] - (a[0] + t * dx), point[1] - (a[1] + t * dy));
}

function segmentsDistanceMeters(a, b, c, d) {
  if (segmentIntersectionT(a, b, c, d).length) return 0;
  const referenceLatitude = (a[1] + b[1] + c[1] + d[1]) / 4;
  const pa = projectMeters(a, referenceLatitude);
  const pb = projectMeters(b, referenceLatitude);
  const pc = projectMeters(c, referenceLatitude);
  const pd = projectMeters(d, referenceLatitude);
  return Math.min(
    pointToSegmentDistance(pa, pc, pd),
    pointToSegmentDistance(pb, pc, pd),
    pointToSegmentDistance(pc, pa, pb),
    pointToSegmentDistance(pd, pa, pb)
  );
}

function segmentDistanceToGeometryMeters(a, b, geometry) {
  if (pointInGeometry(a, geometry) || pointInGeometry(b, geometry)) return 0;
  let minimum = Number.POSITIVE_INFINITY;
  for (const polygon of polygonList(geometry)) {
    for (const ring of polygon) {
      for (let index = 1; index < ring.length; index += 1) {
        minimum = Math.min(
          minimum,
          segmentsDistanceMeters(a, b, ring[index - 1], ring[index])
        );
      }
      if (ring.length > 2) {
        minimum = Math.min(
          minimum,
          segmentsDistanceMeters(a, b, ring[ring.length - 1], ring[0])
        );
      }
    }
  }
  return minimum;
}

function featureZoneType(feature) {
  const properties = feature?.properties || {};
  return normalizeZoneType(
    properties.zoneType ?? properties.zone_type ?? properties.regime ?? properties.tipo
  );
}

function featureId(feature, index) {
  return String(feature?.id ?? feature?.properties?.id ?? `zone-${index}`);
}

export function validateProtectedZonesGeoJson(input) {
  const collection = input?.type === "FeatureCollection" ? input : null;
  if (!collection) return { valid: false, errors: ["GeoJSON deve ser uma FeatureCollection."] };
  const errors = [];
  collection.features.forEach((feature, index) => {
    if (!polygonList(feature?.geometry).length) {
      errors.push(`Feature ${index}: apenas Polygon ou MultiPolygon são suportados.`);
    }
    if (!featureZoneType(feature)) {
      errors.push(`Feature ${index}: tipo de zona ausente ou desconhecido.`);
    }
  });
  return { valid: errors.length === 0, errors };
}

export function intersectRouteWithProtectedZones(route = [], geoJson) {
  const points = route.map(normalizeCoordinate).filter(Boolean);
  if (points.length < 2) return [];
  const validation = validateProtectedZonesGeoJson(geoJson);
  if (!validation.valid) {
    throw new Error(`protected_zones_invalid: ${validation.errors.join(" ")}`);
  }

  const bounds = routeBounds(points);
  return geoJson.features
    .filter((feature) => boundsIntersect(bounds, geometryBounds(feature)))
    .map((feature, featureIndex) => {
      let distanceMeters = 0;
      let firstRouteIndex = null;
      let lastRouteIndex = null;
      const entryPoints = [];
      const exitPoints = [];

      for (let index = 1; index < points.length; index += 1) {
        const a = points[index - 1];
        const b = points[index];
        const cuts = [0, ...boundaryIntersections(a, b, feature.geometry), 1]
          .sort((x, y) => x - y)
          .filter((value, cutIndex, all) => cutIndex === 0 || value - all[cutIndex - 1] > EPSILON);

        for (let cutIndex = 1; cutIndex < cuts.length; cutIndex += 1) {
          const startT = cuts[cutIndex - 1];
          const endT = cuts[cutIndex];
          if (endT - startT <= EPSILON) continue;
          const midpoint = interpolate(a, b, (startT + endT) / 2);
          if (!pointInGeometry(midpoint, feature.geometry)) continue;
          const start = interpolate(a, b, startT);
          const end = interpolate(a, b, endT);
          distanceMeters += haversineMeters(start, end);
          if (firstRouteIndex === null) {
            firstRouteIndex = index - 1;
            entryPoints.push(start);
          }
          lastRouteIndex = index;
          exitPoints[0] = end;
        }
      }

      if (distanceMeters <= 0) return null;
      return {
        featureId: featureId(feature, featureIndex),
        zoneType: featureZoneType(feature),
        name: String(feature?.properties?.name || feature?.properties?.nome || ""),
        distanceMeters,
        firstRouteIndex,
        lastRouteIndex,
        entryPoint: entryPoints[0] || null,
        exitPoint: exitPoints[0] || null,
        properties: feature.properties || {},
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.firstRouteIndex - b.firstRouteIndex);
}

// A margem é apenas operacional: alerta o utilizador para a proximidade e
// absorve imprecisão normal do GPS. Nunca transforma uma proximidade numa
// afirmação jurídica de que o percurso entrou na zona.
export function findRouteProximityToProtectedZones(
  route = [],
  geoJson,
  safetyMarginMeters = 25
) {
  const points = route.map(normalizeCoordinate).filter(Boolean);
  const margin = Math.max(0, Number(safetyMarginMeters) || 0);
  if (points.length < 2 || margin <= 0) return [];
  const validation = validateProtectedZonesGeoJson(geoJson);
  if (!validation.valid) {
    throw new Error(`protected_zones_invalid: ${validation.errors.join(" ")}`);
  }
  const intersections = new Set(
    intersectRouteWithProtectedZones(points, geoJson).map((item) => item.featureId)
  );
  const bounds = routeBounds(points, margin);

  return geoJson.features
    .filter((feature) => boundsIntersect(bounds, geometryBounds(feature)))
    .map((feature, featureIndex) => {
      const id = featureId(feature, featureIndex);
      if (intersections.has(id)) return null;
      let distanceMeters = Number.POSITIVE_INFINITY;
      let nearestRouteIndex = null;
      for (let index = 1; index < points.length; index += 1) {
        const distance = segmentDistanceToGeometryMeters(
          points[index - 1],
          points[index],
          feature.geometry
        );
        if (distance < distanceMeters) {
          distanceMeters = distance;
          nearestRouteIndex = index - 1;
        }
      }
      if (!Number.isFinite(distanceMeters) || distanceMeters > margin) return null;
      return {
        featureId: id,
        zoneType: featureZoneType(feature),
        name: String(feature?.properties?.name || feature?.properties?.nome || ""),
        distanceMeters,
        nearestRouteIndex,
        safetyMarginMeters: margin,
        properties: feature.properties || {},
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
}

export function assessRouteInPnpg({
  route,
  geoJson,
  activity,
  participantCount,
  organized = false,
  safetyMarginMeters = 25,
} = {}) {
  const intersections = intersectRouteWithProtectedZones(route, geoJson);
  const proximities = findRouteProximityToProtectedZones(
    route,
    geoJson,
    safetyMarginMeters
  );
  const zones = intersections.map((intersection) => ({
    ...intersection,
    assessment: evaluatePnpgRule({
      zoneType: intersection.zoneType,
      activity,
      participantCount,
      organized,
    }),
  }));
  return {
    intersections: zones,
    proximities,
    overall: combineZoneAssessments(zones.map((item) => item.assessment)),
    source: PNPG_SOURCE,
  };
}

